import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useContentStudioApi } from '../../../common/hooks/use-content-studio-api.hook';
import { CsAgentRun } from '../../../common/models/content-studio.model';
import { useAgentWorkSignal } from './agent-work.store';
import { useAgentWork } from './use-agent-work.hook';
import { isConversationTurn, profileLabelKey, runRoute } from './work-presentation';

/**
 * Agent work, announced wherever the vendor happens to be.
 *
 * Runs are durable and survive navigation, which is the point of them — but it
 * also means the page that started one is usually not the page you are on when
 * it finishes. Without this, work completed in silence and the only way to find
 * out was to go back and look.
 *
 * Mounted once, beside the assistant panel. It is deliberately the only extra
 * consumer of `useAgentWork`: it publishes what is running to the store so a
 * page can spin its own button without starting a second poll.
 *
 * Renders nothing — sonner owns the surface.
 */
/**
 * How long a job runs before it is worth interrupting for.
 *
 * Anything that finishes inside this was over before the vendor could have
 * walked away from it, so announcing it would have been an interruption about
 * nothing — and the "finished" toast that follows would arrive after they had
 * already seen the result.
 */
const ANNOUNCE_AFTER_MS = 8000;

export const AgentWorkToasts = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const studio = useContentStudioApi();
    const { running, done, paused, reload } = useAgentWork();
    const setRunningProfiles = useAgentWorkSignal((s) => s.setRunningProfiles);

    /** Runs we have already put on screen, so a poll does not re-announce them. */
    const announced = useRef<Set<string>>(new Set());
    /** Runs currently showing a spinner, and the profile each one was. */
    const live = useRef<Map<string, string>>(new Map());
    /** Paused runs already asked about, so the question is not repeated. */
    const asked = useRef<Set<string>>(new Set());

    // Published for buttons elsewhere. Sorted so the store's equality check
    // is not defeated by the API returning the same set in a different order.
    useEffect(() => {
        setRunningProfiles([...new Set(running.map((r) => r.profile))].sort());
    }, [running, setRunningProfiles]);

    useEffect(() => {
        const label = (run: CsAgentRun) => t(profileLabelKey(run.profile));

        /*
         * Somewhere to go, on the running toast as well as the finished one.
         *
         * The whole reason these runs are durable is that the vendor leaves the
         * page — so the moment they are most likely to be somewhere else is
         * while it is still working, which was the one state with no way back.
         * A run with no page of its own (a studio run before its project
         * exists) simply gets no button.
         */
        const linkTo = (run: CsAgentRun) => {
            const route = runRoute(run);
            return route
                ? { label: t('WORK.TOAST.VIEW') as string, onClick: () => navigate(route) }
                : undefined;
        };

        /*
         * Stopping, from wherever the vendor sees the work.
         *
         * A run that is spending credits needs to be stoppable from the same
         * place it is announced — the toast is often the only sight of it, and
         * hunting for the Work page while a job burns through a balance is not
         * a real way out.
         */
        const stop = (run: CsAgentRun) => ({
            label: t('WORK.TOAST.STOP') as string,
            onClick: () => {
                void studio.cancelAgentRun(run.runId)
                    .then(() => {
                        toast.message(t('WORK.TOAST.STOPPED', { work: label(run) }), { id: run.runId });
                        return reload();
                    })
                    .catch(() => undefined);
            },
        });

        for (const run of running) {
            if (announced.current.has(run.runId)) continue;

            // A reply appears where it was asked for. See isConversationTurn.
            if (isConversationTurn(run)) continue;

            /*
             * Not yet: given a moment, most runs finish on their own. Skipped
             * WITHOUT marking it announced, so the next poll reconsiders — a
             * job that is genuinely long gets its toast a few seconds late,
             * which is exactly when it starts being worth having.
             */
            const since = Date.now() - new Date(run.startedAt || run.createdAt).getTime();
            if (since < ANNOUNCE_AFTER_MS) continue;

            announced.current.add(run.runId);
            live.current.set(run.runId, run.profile);

            /*
             * Keyed by run id, so the finish below replaces this toast rather
             * than stacking a second one beneath it. No duration: a loading
             * toast stays until it is resolved or dismissed.
             */
            toast.loading(t('WORK.TOAST.RUNNING', { work: label(run) }), {
                id: run.runId,
                description: t('WORK.TOAST.RUNNING_HINT') as string,
                action: linkTo(run),
                cancel: stop(run),
            });
        }

        /*
         * It has spent its allowance and stopped to ask.
         *
         * Asked here rather than left to the Work page because the answer is
         * time-sensitive in one direction only: the run is holding a
         * conversation it will lose, and the vendor is the only one who knows
         * whether the job is worth another allowance. Never auto-dismissed —
         * an unanswered question that quietly disappears is the same as
         * stopping the run without telling anyone.
         */
        for (const run of paused) {
            if (asked.current.has(run.runId)) continue;
            asked.current.add(run.runId);
            live.current.delete(run.runId);

            toast.warning(t('WORK.TOAST.PAUSED', { work: label(run) }), {
                id: run.runId,
                description: t('WORK.TOAST.PAUSED_HINT', { credits: run.creditsSpent ?? 0 }) as string,
                duration: Infinity,
                action: {
                    label: t('WORK.TOAST.CONTINUE') as string,
                    onClick: () => {
                        void studio.resumeAgentRun(run.runId)
                            .then(() => {
                                asked.current.delete(run.runId);
                                announced.current.delete(run.runId);
                                toast.message(t('WORK.TOAST.RESUMED', { work: label(run) }), { id: run.runId });
                                // Puts the poll back on, so the resumed run is
                                // picked up and announced like any other.
                                useAgentWorkSignal.getState().runStarted();
                            })
                            .catch(() => undefined);
                    },
                },
                cancel: stop(run),
            });
        }

        for (const run of done) {
            if (!live.current.has(run.runId)) continue;
            live.current.delete(run.runId);

            const action = linkTo(run);

            if (run.status === 'failed') {
                toast.error(t('WORK.TOAST.FAILED', { work: label(run) }), { id: run.runId, action });
            } else {
                toast.success(t('WORK.TOAST.DONE', { work: label(run) }), {
                    id: run.runId,
                    action,
                });
            }
        }
    }, [running, done, paused, navigate, reload, studio, t]);

    return null;
};
